import { useCallback, useRef, useState } from 'react';

export const DATE_PICKER_VIEWS = ['year', 'month', 'day'];

// A year or month click updates the value and unmounts that grid before the
// popup decides whether to close. Hold the in-progress date locally and ignore
// that close so the month (then the day) can still be chosen.
export const useStableDatePickerPopup = ({
  value,
  onChange,
  open: openProp,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  openTo = 'day',
} = {}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const openRef = useRef(open);
  const viewRef = useRef(openTo);
  const suppressCloseRef = useRef(false);

  openRef.current = open;

  const keepOpenThroughViewChange = () => {
    suppressCloseRef.current = true;
    queueMicrotask(() => {
      suppressCloseRef.current = false;
    });
  };

  const onOpen = useCallback(() => {
    viewRef.current = openTo;
    if (!isControlled) {
      setUncontrolledOpen(true);
    }
    onOpenProp?.();
  }, [isControlled, onOpenProp, openTo]);

  const onViewChange = useCallback((nextView) => {
    viewRef.current = nextView;
    if (nextView === 'month' || nextView === 'day') {
      keepOpenThroughViewChange();
    }
  }, []);

  const onClose = useCallback(() => {
    if (suppressCloseRef.current) {
      suppressCloseRef.current = false;
      return;
    }
    setDraft(null);
    viewRef.current = openTo;
    if (!isControlled) {
      setUncontrolledOpen(false);
    }
    onCloseProp?.();
  }, [isControlled, onCloseProp, openTo]);

  const handleChange = useCallback((nextValue, context) => {
    if (openRef.current && viewRef.current !== 'day') {
      keepOpenThroughViewChange();
      setDraft(nextValue);
      return;
    }
    setDraft(null);
    onChange?.(nextValue, context);
  }, [onChange]);

  return {
    open,
    onOpen,
    onClose,
    onViewChange,
    value: draft ?? value,
    onChange: handleChange,
    views: DATE_PICKER_VIEWS,
  };
};
